import React, { useState } from 'react';
import './ContactUs.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faHome, faPhone } from '@fortawesome/free-solid-svg-icons';

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;
const WHATSAPP_NUMBER = '919928620516';
const WHATSAPP_MESSAGE =
  'Hello Maxfort Education School, I would like to enquire about admissions and school details.';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phonenumber: '',
    message: '',
    botcheck: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!WEB3FORMS_ACCESS_KEY) {
      setStatus({
        type: 'error',
        message:
          'Form service is not configured yet. Add REACT_APP_WEB3FORMS_ACCESS_KEY to your .env.local file and restart the app.'
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const submission = new FormData();
      submission.append('access_key', WEB3FORMS_ACCESS_KEY);
      submission.append('subject', 'New Contact Message from Maxfort Education Website');
      submission.append('from_name', 'Maxfort Education Website');
      submission.append('replyto', formData.email);
      submission.append('name', formData.name);
      submission.append('email', formData.email);
      submission.append('phonenumber', formData.phonenumber);
      submission.append('message', formData.message);
      submission.append('botcheck', formData.botcheck);

      const response = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        body: submission
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Unable to submit the form right now.');
      }

      setStatus({
        type: 'success',
        message: 'Your message has been sent. The school team will get back to you soon.'
      });
      setFormData({
        name: '',
        email: '',
        phonenumber: '',
        message: '',
        botcheck: ''
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Message could not be sent. ${error.message || 'Please try again later.'}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="contact-hero__copy">
          <span className="contact-hero__eyebrow">Contact Maxfort</span>
          <h1>Let&apos;s make the first interaction feel as strong as the campus visit.</h1>
          <p>
            Reach out for admissions, campus visits, or general queries. We&apos;ll help you with
            the next step quickly and clearly.
          </p>
        </div>

        <div className="contact-hero__card">
          <strong>Admissions support</strong>
          <span>Monday to Saturday</span>
          <p>8:00 AM to 2:00 PM</p>
          <a href="tel:+919928620516">+91 99286 20516</a>
        </div>
      </section>

      <section className="contact-grid">
        <div className="contact-panel contact-panel--info">
          <div className="contact-panel__heading">
            <span className="contact-panel__eyebrow">Visit or call</span>
            <h2>We&apos;re easy to reach.</h2>
          </div>

          <div className="contact-list">
            <div className="contact-item">
              <div className="contact-item__icon">
                <FontAwesomeIcon icon={faHome} />
              </div>
              <div>
                <h3>Campus Address</h3>
                <p>Bedwa Lamps Street, Near Vaghar Nagar, Partapur, Rajasthan - 327024</p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-item__icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <div>
                <h3>Call Us</h3>
                <p>+91 9928620516</p>
                <span>Admissions desk available Monday to Saturday.</span>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-item__icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <div>
                <h3>Email Us</h3>
                <p>maxforteducationschool@gmail.com</p>
                <span>Use the form for queries, visits, and follow-up requests.</span>
              </div>
            </div>
          </div>

          <div className="location">
            <iframe
              title="Maxfort Education School location"
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14624.648272889992!2d74.1762136!3d23.5985199!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x93b9d1620d627ed7!2sMaxfort%20Education%20School!5e0!3m2!1sen!2sin!4v1627985108540!5m2!1sen!2sin"
              loading="lazy"
              allowFullScreen
            ></iframe>
          </div>
        </div>

        <div className="contact-panel">
          <div className="contact-panel__heading">
            <span className="contact-panel__eyebrow">Send an enquiry</span>
            <h2>Tell us what you need.</h2>
          </div>

          <div className="contact-alt">
            <p>If the contact form is unavailable, use WhatsApp for a direct response.</p>
            <a
              className="contact-alt__whatsapp"
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
              target="_blank"
              rel="noreferrer"
            >
              <i className="fab fa-whatsapp" aria-hidden="true"></i>
              <span>Chat on WhatsApp</span>
            </a>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <input
              type="checkbox"
              name="botcheck"
              className="contact-form__botcheck"
              tabIndex="-1"
              autoComplete="off"
              checked={Boolean(formData.botcheck)}
              onChange={(event) =>
                setFormData((current) => ({
                  ...current,
                  botcheck: event.target.checked ? 'checked' : ''
                }))
              }
            />

            <div className="contact-form__grid">
              <label>
                <span>Full Name</span>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </label>

              <label>
                <span>Email</span>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </label>
            </div>

            <label>
              <span>Phone Number</span>
              <input
                type="text"
                name="phonenumber"
                placeholder="Enter your phone number"
                value={formData.phonenumber}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              <span>Message</span>
              <textarea
                rows="7"
                name="message"
                placeholder="Write your message"
                value={formData.message}
                onChange={handleChange}
                required
              ></textarea>
            </label>

            {status.message ? (
              <div className={`contact-status contact-status--${status.type}`}>{status.message}</div>
            ) : null}

            <button type="submit" className="contact-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
